import React from "react";
import { X, CheckCircle2, AlertTriangle, Sparkles, Lightbulb } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

function Section({ icon: Icon, color, title, children, testId }) {
  return (
    <div className="mb-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className="h-4 w-4" style={{ color }} />
        <p className="text-sm font-bold text-slate-900">{title}</p>
      </div>
      {children}
    </div>
  );
}

function TagList({ items, tone }) {
  const style = tone === "amber"
    ? { backgroundColor: "rgba(245,158,11,0.1)", color: "#F59E0B" }
    : { backgroundColor: "rgba(2,140,248,0.1)", color: "#028CF8" };
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, i) => (
        <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={style}>{item}</span>
      ))}
    </div>
  );
}

export default function JobMatchModal({ analysis, onClose, viewerRole = "candidate", candidateName = "" }) {
  const { t } = useLanguage();
  const b = analysis.breakdown;
  const score = analysis.score;
  const color = score >= 70 ? "#028CF8" : score >= 45 ? "#F59E0B" : "#64748B";
  const EXP_LABELS = { entry: t("jobCard.expEntry"), mid: t("jobCard.expMid"), senior: t("jobCard.expSenior"), executive: t("jobCard.expExecutive") };

  // Employer perspective ("Ana tiene X skills...") vs candidate perspective
  // ("Tienes X skills...") — same analysis data, only the wording changes.
  const isEmployer = viewerRole === "employer";
  const tm = (key, params = {}) => {
    let str = t(isEmployer ? `jobMatch.employer.${key}` : `jobMatch.${key}`);
    if (isEmployer) params = { name: candidateName, ...params };
    Object.entries(params).forEach(([k, v]) => { str = str.replace(`{${k}}`, v); });
    return str;
  };

  const strengths = [];
  if (b.skills.max && b.skills.matched.length > 0 && b.skills.score / b.skills.max >= 0.6) strengths.push(tm("strengthSkills", { count: b.skills.matched.length }));
  if (b.experience.score === b.experience.max) strengths.push(tm("strengthExperience"));
  if (b.location.detail === "remote") strengths.push(tm("strengthRemote"));
  if (b.location.detail === "same_city") strengths.push(tm("strengthLocation"));
  if (b.requirements.total > 0 && b.requirements.met === b.requirements.total) strengths.push(tm("strengthAllReqs"));
  if (analysis.relevant_experience.length > 0) strengths.push(tm("strengthRelevantExp", { position: analysis.relevant_experience[0].position }));
  if (b.work_mode?.evaluated && b.work_mode.score > 0) strengths.push(tm("strengthWorkMode"));
  if (b.salary_fit?.evaluated && b.salary_fit.score >= b.salary_fit.max) strengths.push(tm("strengthSalary"));

  const recommendations = [];
  if (analysis.missing_keywords.length > 0) recommendations.push(tm("recoKeywords", { list: analysis.missing_keywords.slice(0, 4).join(", ") }));
  if (analysis.unmet_requirements.length > 0) recommendations.push(tm("recoRequirements", { req: analysis.unmet_requirements[0] }));
  if (b.experience.score < b.experience.max) {
    const jobLevel = EXP_LABELS[b.experience.job_level] || b.experience.job_level;
    recommendations.push(tm("recoExperience", { level: jobLevel }));
  }
  if (analysis.matched_skills.length > 0) recommendations.push(tm("recoHighlight", { skill: analysis.matched_skills[0] }));
  if (b.location.detail === "different_city") recommendations.push(tm("recoRelocate"));
  if (b.location.evaluated === false) recommendations.push(tm("recoLocationUnknown"));
  if (b.work_mode?.evaluated && b.work_mode.score === 0) recommendations.push(tm("recoWorkMode"));
  if (b.salary_fit?.evaluated && b.salary_fit.score === 0) recommendations.push(tm("recoSalaryBelow"));
  if ((analysis.signals || []).some((s) => s.code === "availability_unknown")) recommendations.push(tm("recoAvailability"));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="match-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-poppins font-bold text-lg text-slate-900">{t("jobMatch.modalTitle")}</h3>
          <button onClick={onClose} data-testid="match-modal-close" className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-center mb-6 pb-5 border-b border-slate-100">
          <p className="text-4xl font-poppins font-bold" style={{ color }} data-testid="match-modal-score">{score}%</p>
          <p className="text-sm text-slate-700">{t("jobMatch.compatible")}</p>
          {analysis.evaluated_max_points < 100 && (
            <p className="text-xs text-slate-600 mt-1.5" data-testid="match-evaluated-note">
              {t("jobMatch.evaluatedNote").replace("{max}", analysis.evaluated_max_points)}
            </p>
          )}
        </div>

        {analysis.matched_skills.length > 0 && (
          <Section icon={CheckCircle2} color="#028CF8" title={t("jobMatch.matchesTitle")} testId="match-section-matches">
            <TagList items={analysis.matched_skills} tone="blue" />
          </Section>
        )}

        {(analysis.unmet_requirements.length > 0 || analysis.missing_keywords.length > 0) && (
          <Section icon={AlertTriangle} color="#F59E0B" title={t("jobMatch.gapsTitle")} testId="match-section-gaps">
            {analysis.unmet_requirements.length > 0 && (
              <ul className="space-y-1 mb-2">
                {analysis.unmet_requirements.map((req, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                    <span className="text-[#F59E0B] mt-0.5">•</span> {req}
                  </li>
                ))}
              </ul>
            )}
            {analysis.missing_keywords.length > 0 && <TagList items={analysis.missing_keywords} tone="amber" />}
          </Section>
        )}

        {strengths.length > 0 && (
          <Section icon={Sparkles} color="#028CF8" title={t("jobMatch.strengthsTitle")} testId="match-section-strengths">
            <ul className="space-y-1.5">
              {strengths.map((s, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                  <span className="text-[#028CF8] mt-0.5">✓</span> {s}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {recommendations.length > 0 && (
          <Section icon={Lightbulb} color="#64748B" title={t("jobMatch.recoTitle")} testId="match-section-recommendations">
            <ul className="space-y-1.5">
              {recommendations.map((r, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                  <span className="text-slate-400 mt-0.5">→</span> {r}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
}
